import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2/dist/sweetalert2';
import 'sweetalert2/src/sweetalert2.scss';
import { useServices } from '@services';
import { QuizForm } from './QuizForm';
import { QuestionForm } from './QuestionForm';

export const QuizItem = ({ quiz, refreshed }) => {
  const { db, auth } = useServices();
  const [open, setOpen] = useState(false);
  const [questions, setQuestions] = useState();
  const [loading, setLoading] = useState(false);
  const [role, setRole] = useState('');
  const [quizModal, setQuizModal] = useState(null);
  const [questionModal, setQuestionModal] = useState(null);

  useEffect(() => {
    auth.currentUser.getIdTokenResult().then(token => {
      setRole(token?.claims?.role || '');
    });
  }, [auth]);

  const toggle = () => {
    if (!open && !questions) {
      setLoading(true);
      db.collection('quizData')
        .doc(quiz.id)
        .collection('questions')
        .get()
        .then(snapshot => {
          setQuestions(
            snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
          );
        })
        .catch(err => {
          console.error('Failed to load questions', {
            path: `quizData/${quiz.id}/questions`,
            code: err.code,
            message: err.message,
          });
          toast.error(`Failed to load questions: ${err.code || err.message}`);
        })

      .finally(() => setLoading(false));
    }
    setOpen(!open);
  };

  const refreshQuestions = () => {
    setLoading(true);
    db.collection('quizData')
      .doc(quiz.id)
      .collection('questions')
      .get()
      .then(snapshot => {
        setQuestions(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      })
      .catch(() => toast.error('Failed to load questions'))
      .finally(() => setLoading(false));
  };

  const canEdit = role === 'admin' || role === 'editor';

  return (
    <div className="border-b">
      <div className="flex items-center p-4">
        <button className="flex-1 text-left focus:outline-none" onClick={toggle}>
          <i className={`la ${open ? 'la-angle-down' : 'la-angle-right'} text-2xl mr-4`}></i>
          <span className="font-medium mr-2">{quiz.name}</span>
        </button>
        <span className="text-sm text-gray-500 mr-4">{quiz.questionCount} questions</span>
        {canEdit && (
          <button onClick={() => setQuizModal(quiz)} className="text-primary-500 mr-2">
            Edit
          </button>
        )}
        {canEdit && (
          <button
            onClick={() => {
              Swal.fire({
                title: 'Are you sure?',
                text: 'Delete this quiz?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, delete it!',
                cancelButtonText: 'No, keep it',
              }).then(result => {
                if (!result.value) return;
                db
                  .collection('quizData')
                  .doc(quiz.id)
                  .delete()
                  .then(() => db.updateQuizModifiedOn())
                  .then(refreshed)
                  .catch(() => toast.error('Failed to delete quiz'));
              });
            }}
            className="text-primary-500"
          >
            Delete
          </button>
        )}
      </div>
      {open && (
        <div className="pl-10 pr-4 pb-4">
          {canEdit && (
            <button onClick={() => setQuestionModal({})} className="text-primary-500 mb-2">
              Add Question
            </button>
          )}
          {loading && <div className="text-center">Loading...</div>}
          {questions &&
            questions.map(question => (
              <div key={question.id} className="mb-4">
                <div className="font-medium mb-1 flex items-center">
                  <span className="flex-1">{question.text}</span>
                  {canEdit && (
                    <>
                      <button onClick={() => setQuestionModal(question)} className="text-primary-500 mr-2">
                        Edit
                      </button>
                      <button
                        onClick={() => {
                          Swal.fire({
                            title: 'Are you sure?',
                            text: 'Delete this question?',
                            icon: 'warning',
                            showCancelButton: true,
                            confirmButtonText: 'Yes, delete it!',
                            cancelButtonText: 'No, keep it',
                          }).then(result => {
                            if (!result.value) return;
                            db
                              .deleteQuizQuestion(quiz.id, question.id)
                              .then(() => db.updateQuestionCount(quiz.id))
                              .then(() => db.updateQuizModifiedOn())
                              .then(refreshQuestions)
                              .catch(() => toast.error('Failed to delete question'));
                          });
                        }}
                        className="text-primary-500"
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
                <ul className="list-disc ml-6">
                  {question.optionsOrder &&
                    question.optionsOrder.map(optId => (
                      <li
                        key={optId}
                        className={optId === question.correctOptionId ? 'font-bold' : ''}
                      >
                        {question.options?.[optId]?.text}
                      </li>
                    ))}
                </ul>
              </div>
            ))}
          {questions && questions.length === 0 && (
            <div className="text-gray-500">No questions</div>
          )}
        </div>
      )}
      <QuizForm quiz={quizModal} onClose={() => setQuizModal(null)} saved={refreshed} />
      <QuestionForm
        quizId={quiz.id}
        question={questionModal}
        onClose={() => setQuestionModal(null)}
        saved={() => {
          refreshQuestions();
          refreshed();
        }}
      />
    </div>
  );
};
